import axios from 'axios';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { AlertContext } from '../../App';
import { Point, UserInfo } from '../index';
import PointItem from '../PointItem';
import { getErrorMessage } from '../../util/message';
import { translatePlaceDescription } from '../../util';

import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

interface PointHistoryRequestPayload {
  offset?: number;
  uuid: string;
}

interface PointHistoryResponse {
  place: {
    id: string;
    name: string;
    payable_point_policy: {
      minimum_required_point: number | null;
    };
    rewards: {
      cost: number;
      name: string;
    }[];
  };
  point: {
    created_at: string;
    last_visited_at: string;
    points: number;
    saved_points: number;
  };
}

const LIMIT = 100;

const getPointHistory = async (payload: PointHistoryRequestPayload): Promise<Point[]> => {
  const {
        offset = 0,
        uuid,
    } = payload;

  const config = {
    params: {
      offset,
      limit: LIMIT,
    },
  };
  const response = await axios.get<PointHistoryResponse[]>(`/mobile/users/${uuid}/points/`, config);
  const { data } = response;

  const points = data.map<Point>(point => ({
    place: {
      id: point.place.id,
      name: point.place.name,
      payablePointPolicy: {
        minimumRequiredPoint: point.place.payable_point_policy.minimum_required_point,
      },
      rewards: point.place.rewards,
    },
    point: {
      createdAt: point.point.created_at,
      lastVisitedAt: point.point.last_visited_at,
      points: point.point.points,
      savedPoints: point.point.saved_points,
    },
  }));

  return points;
};

interface Props {
  onClick?(point: Point): void;
  userInfo: UserInfo;
}

const PointHistory: React.FC<Props> = props => {
  const { t } = useTranslation();
  const { onClick, userInfo } = props;
  const alert = useContext(AlertContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [points, setPoints] = useState<Point[]>([]);
  const handleFailure = useCallback((message: string) => {
    alert({
      content: message,
      type: 'error',
    });
  }, [alert]);

  const fetchPointHistory = async (offset: number) => {
    setLoading(true);
    const payload: PointHistoryRequestPayload = {
      offset: (offset - 1) * LIMIT,
      uuid: userInfo.uuid,
    };

    try {
      const newPoints = await getPointHistory(payload);
      setPoints(points.concat(newPoints));
      setHasMore(newPoints.length > 0 && newPoints.length % LIMIT === 0);
    } catch (err) {
      handleFailure(getErrorMessage(err));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchPointHistory(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
        <div className={styles.pointHistory}>
            <div className={styles.category}>
                <p>{t('내 포인트')}</p>
            </div>
            <InfiniteScroll
                pageStart={1}
                loadMore={fetchPointHistory}
                hasMore={!loading && hasMore}
                initialLoad={false}
            >
                {points.map((item, index) => {
                  return (
                        <PointItem
                            key={index}
                            store={item.place.name}
                            description={translatePlaceDescription(item)}
                            point={item.point.points}
                            className={styles.pointItem}
                            onClick={() => onClick && onClick(item)}
                        />
                  );
                })}
            </InfiniteScroll>
        </div>
  );
};

export default PointHistory;
